import { useMemo, useState } from 'react';
import { getLiturgicalDay } from '@orthodox-way/liturgical-engine';
import { useReveal } from '../hooks/useReveal.js';

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDate(iso: string): string {
  const parts = iso.split('-').map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function pretty(slug: string): string {
  return slug.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function LiturgicalEngine() {
  const leftRef = useReveal<HTMLDivElement>(0.3);
  const rightRef = useReveal<HTMLDivElement>(0.3);
  const [iso, setIso] = useState<string>(() => todayIso());
  const [pulse, setPulse] = useState(false);

  const day = useMemo(() => {
    try {
      return getLiturgicalDay(iso);
    } catch {
      return null;
    }
  }, [iso]);

  const onChange = (v: string) => {
    setPulse(true);
    setIso(v);
    window.setTimeout(() => setPulse(false), 400);
  };

  return (
    <section
      id="engine"
      className="relative w-full"
      style={{
        padding: '76px 20px',
        background: 'var(--ow-bg-subtle)',
      }}
    >
      <div className="mx-auto max-w-container grid lg:grid-cols-[2fr_3fr] gap-12 items-center">
        <div ref={leftRef} className="ow-reveal">
          <p
            className="font-display uppercase text-gold"
            style={{ fontSize: 11, letterSpacing: '0.32em' }}
          >
            § IV - The Heart
          </p>
          <h2
            className="font-display text-primary mt-5"
            style={{
              fontSize: 'clamp(32px, 4.5vw, 52px)',
              fontWeight: 500,
              letterSpacing: '0.02em',
              lineHeight: 1.15,
            }}
          >
            The Liturgical Engine
          </h2>
          <div className="mt-8 space-y-5 text-secondary" style={{ fontSize: 17, lineHeight: 1.75 }}>
            <p>
              Behind every screen of Orthodox Way is a single piece of computation: a function that,
              given a date, knows what the Church knows.
            </p>
            <p>
              The Pascha cycle, calculated by the Julian Paschalion. The eight tones of the
              Octoechos, anchored to the All Saints Sunday. The four major fasts, with their
              feast-day exceptions. Every commemoration, every reading, every troparion, resolved
              in a few milliseconds.
            </p>
            <p>It runs on your phone, offline, accurate from 1900 to 2200.</p>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-6">
            <Stat label="Years of accurate Pascha" value="1900–2200" />
            <Stat label="Octoechos cycle" value="8 Tones" />
            <Stat label="Major fasts" value="4 Fasts" />
          </div>
        </div>

        {/* Interactive card */}
        <div ref={rightRef} className="ow-reveal">
          <div
            className="ow-mockup bg-bg-elevated border border-border rounded-card"
            style={{
              padding: 32,
              boxShadow: 'var(--ow-shadow-md)',
              transition:
                'box-shadow 200ms var(--ow-ease-soft), transform 400ms var(--ow-ease-soft)',
              ...(pulse
                ? { boxShadow: '0 0 0 1px var(--ow-gold), 0 24px 60px var(--ow-gold-glow)' }
                : {}),
            }}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p
                className="font-display uppercase text-gold"
                style={{ fontSize: 10, letterSpacing: '0.32em' }}
              >
                Today
              </p>
              <label className="inline-flex items-center gap-2">
                <span className="sr-only">Choose a date</span>
                <input
                  type="date"
                  id="liturgical-demo-date"
                  name="liturgical-demo-date"
                  value={iso}
                  onChange={(e) => onChange(e.target.value)}
                  className="bg-transparent border border-border-strong rounded-sm px-3 py-1.5 text-secondary focus:border-gold focus:outline-hidden"
                  style={{ fontSize: 12 }}
                />
              </label>
            </div>

            <div
              key={iso}
              className="mt-5"
              style={{ animation: 'ow-fade-up 400ms var(--ow-ease-soft) both' }}
            >
              <h3
                className="font-display text-primary"
                style={{ fontSize: 28, fontWeight: 500, letterSpacing: '0.02em' }}
              >
                {formatDate(iso)}
              </h3>

              {day ? (
                <>
                  <div className="mt-5 flex flex-wrap items-center gap-2">
                    <Chip label={day.tone.tone ? `Tone ${day.tone.tone}` : day.tone.season} />
                    <Chip label={day.tone.season} muted />
                  </div>

                  {day.primaryFeast && (
                    <p
                      className="scripture text-secondary mt-5"
                      style={{ fontSize: 19, lineHeight: 1.55 }}
                    >
                      {pretty(day.primaryFeast.slug)}
                    </p>
                  )}

                  <div className="mt-6">
                    <p
                      className="font-display uppercase text-gold"
                      style={{ fontSize: 9, letterSpacing: '0.3em' }}
                    >
                      Fasting · standard
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <FastChip label="Meat" allowed={day.fasting.standard.allowsMeat} />
                      <FastChip label="Dairy" allowed={day.fasting.standard.allowsDairy} />
                      <FastChip label="Fish" allowed={day.fasting.standard.allowsFish} />
                      <FastChip label="Oil" allowed={day.fasting.standard.allowsOil} />
                      <FastChip label="Wine" allowed={day.fasting.standard.allowsWine} />
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-2 gap-4 border-t border-border pt-5">
                    {day.readings.slice(0, 4).map((r) => (
                      <div key={`${r.type}-${r.reference}`}>
                        <p
                          className="font-display uppercase text-muted"
                          style={{ fontSize: 9, letterSpacing: '0.3em' }}
                        >
                          {r.type === 'epistle'
                            ? 'Epistle'
                            : r.type === 'gospel'
                              ? 'Gospel'
                              : r.type === 'old_testament'
                                ? 'Old Testament'
                                : 'Matins Gospel'}
                        </p>
                        <p className="text-secondary mt-1" style={{ fontSize: 13 }}>
                          {r.reference}
                        </p>
                      </div>
                    ))}
                    {day.readings.length === 0 && (
                      <p className="text-muted col-span-2" style={{ fontSize: 12 }}>
                        No appointed readings.
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-muted mt-6" style={{ fontSize: 14 }}>
                  Select a date.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-l border-gold pl-4">
      <p
        className="font-display text-primary"
        style={{ fontSize: 18, fontWeight: 500, letterSpacing: '0.02em' }}
      >
        {value}
      </p>
      <p className="text-muted mt-1" style={{ fontSize: 11, lineHeight: 1.5 }}>
        {label}
      </p>
    </div>
  );
}

function Chip({ label, muted }: { label: string; muted?: boolean }) {
  return (
    <span
      className={`font-display uppercase ${muted ? 'text-secondary border-border' : 'text-gold border-gold'} border rounded-full px-3 py-1`}
      style={{ fontSize: 10, letterSpacing: '0.18em' }}
    >
      {label}
    </span>
  );
}

function FastChip({ label, allowed }: { label: string; allowed: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1 ${
        allowed ? 'border-success text-success' : 'border-border-strong text-secondary'
      }`}
      style={{ fontSize: 11 }}
    >
      <span aria-hidden>{allowed ? '✓' : '✕'}</span>
      {label}
    </span>
  );
}
